/**
 * Bewijsniveau — hoeveel weten we werkelijk over deze leerling?
 *
 * WAAROM DIT BESTAAT
 * Het leerlab deed persoonlijke uitspraken ("Beste keuze voor jou", "Je kent
 * het al") tegen een leerling die nog nooit een vraag had gemaakt. De oorzaak
 * was niet één verkeerde tekst maar een ontbrekend begrip: nergens werd de
 * vraag gesteld hoeveel er eigenlijk bekend is. De aanbevelingsketen was zo
 * gebouwd dat er ALTIJD iets uitkwam, met een reden die er even stellig
 * bijstond.
 *
 * Deze module is dat ontbrekende begrip, op één plek, zodat elk onderdeel dat
 * een uitspraak over een kind doet hetzelfde antwoord krijgt. Twee onderdelen
 * die het zelf uitrekenen, is precies hoe het scherm zichzelf kan tegenspreken.
 *
 * PER ONDERWERP, NIET PER KIND
 * Een kind kan veel van rekenen weten en niets van spelling. Een globale
 * "nieuw kind"-vlag slaat dat plat en zou een kind advies onthouden over
 * precies dat vak waar het al maanden aan werkt. Daarom is het bewijsniveau in
 * de eerste plaats per familie; `getOverallEvidenceLevel` is een afgeleide
 * daarvan voor schermen die één antwoord nodig hebben.
 */

import type { FamilyMasteryState } from './familyMasteryEngine';
import { getFamilyMasteryKey } from './familyMasteryEngine';

/**
 * Minimum aantal beantwoorde vragen op één onderwerp voordat we er iets over
 * mogen beweren.
 *
 * DIT IS GEEN NIEUW GETAL. Het toetsdomein hanteert dezelfde ondergrens
 * voordat het een niveau-uitspraak doet, en de groei-indicator in het
 * toetsrapport ook. Een tweede, afwijkende drempel zou betekenen dat hetzelfde
 * kind in twee schermen een ander antwoord krijgt op dezelfde vraag — precies
 * de inconsistentie die in het toetsrapport al gerepareerd moest worden (daar
 * had de groei-indicator een ondergrens en de niveauschatting niet).
 *
 * Verhoog dit alleen met een reden die voor beide domeinen geldt.
 */
export const MIN_ANSWERS_FOR_CLAIM = 3;

/**
 * Wat we over een onderwerp (of over het kind als geheel) kunnen zeggen.
 *
 *  • `none`       — geen enkele poging. Uitspraken over het KIND zijn niet
 *                   toegestaan; uitspraken over de LEERSTOF wel ("dit is een
 *                   goed beginpunt in de leerlijn").
 *  • `thin`       — er is iets, maar onder de ondergrens. Een voorstel mag,
 *                   met een reden die de onzekerheid noemt in plaats van hem
 *                   te verzwijgen.
 *  • `sufficient` — vanaf hier is "jouw zwakste onderwerp" een claim en geen
 *                   gok, en mag "voor jou" op een kaart staan.
 */
export type EvidenceLevel = 'none' | 'thin' | 'sufficient';
type MasteryMap = Record<string, Pick<FamilyMasteryState, 'masteryScore' | 'attempts'>>;

/** Bewijsniveau voor één onderwerp binnen één groep. */
export function getEvidenceLevelForFamily(familyMastery: MasteryMap | undefined, familyId: string, group: number): EvidenceLevel {
  const entry = familyMastery?.[getFamilyMasteryKey(familyId, group)];
  if (!entry || entry.attempts <= 0) return 'none';
  return entry.attempts >= MIN_ANSWERS_FOR_CLAIM ? 'sufficient' : 'thin';
}

/**
 * Bewijsniveau voor het kind als geheel, binnen één groep.
 *
 * `sufficient` zodra ÉÉN onderwerp de ondergrens haalt — niet pas wanneer een
 * bepaald aantal onderwerpen dat doet. Wachten op meerdere onderwerpen zou een
 * kind dat één vak grondig heeft geoefend nog steeds generieke voorstellen
 * geven, terwijl we over dat ene vak wél iets weten. Wat er over de ándere
 * onderwerpen wordt beweerd, hoort door `getEvidenceLevelForFamily` te worden
 * begrensd, niet door deze globale waarde.
 */
export function getOverallEvidenceLevel(familyMastery: MasteryMap | undefined, group: number): EvidenceLevel {
  const suffix = `_g${group}`;
  let best: EvidenceLevel = 'none';
  for (const [key, entry] of Object.entries(familyMastery || {})) {
    if (!key.endsWith(suffix)) continue;
    if (entry.attempts <= 0) continue;
    if (entry.attempts >= MIN_ANSWERS_FOR_CLAIM) return 'sufficient';
    best = 'thin';
  }
  return best;
}

/**
 * Aantal onderwerpen binnen deze groep waarover we genoeg weten.
 *
 * Bedoeld om uit te LEGGEN, niet om te beslissen ("je hebt 2 onderwerpen
 * geoefend"). Beslissingen horen via de niveaus te lopen, zodat de drempel op
 * één plek staat.
 */
export function countFamiliesWithEvidence(familyMastery: MasteryMap | undefined, group: number): number {
  const suffix = `_g${group}`;
  let count = 0;
  for (const [key, entry] of Object.entries(familyMastery || {})) {
    if (key.endsWith(suffix) && entry.attempts >= MIN_ANSWERS_FOR_CLAIM) {
      count += 1;
    }
  }
  return count;
}